import { FormDrawer } from "../../../components/FormDrawer";
import { spacing as designSpacing, drawerDefaults } from "../../../app/design-system";
import { CheckOutlined, ClockCircleOutlined, SafetyCertificateOutlined } from "@ant-design/icons";
import { Alert, App, Button, Descriptions, Flex, Space, Tag, Typography } from "../../../../../../../shared/frontend/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "../../../app/i18n/useI18n";
import { formatDateTimeIso } from "../../../app/units/format";
import type { UnitsSettings } from "../../../app/units/units";

import { useFeedback } from "../../../../../../../shared/frontend/feedback/useFeedback";
import { PRIVACY_ACTION, PRIVACY_CHANNEL, PRIVACY_STATUS, type PrivacyStatus } from "./personPrivacyValues";

export type PersonPrivacySummary = {
  status: PrivacyStatus;
  privacyNoticeReceivedAt?: string | null;
  privacyNoticeChannel?: string | null;
  privacyNoticeReceivedByUserId?: string | null;
  privacyNoticeReceivedByUserName?: string | null;
  erasureRequestedAt?: string | null;
  anonymizedAt?: string | null;
  openRequest?: {
    id: string;
    status: string;
    requestedAt: string;
    requestedByUserId?: string | null;
    manualTaskId?: string | null;
    resultNote?: string | null;
  } | null;
  openTask?: {
    id: string;
    title: string;
    description?: string | null;
    createdAt: string;
    dueDate?: string | null;
    status: string;
  } | null;
};

type PersonPrivacyDrawerProps = {
  open: boolean;
  personId: string;
  personName: string;
  api: {
    get<T>(url: string, options?: { suppressGlobalErrorToast?: boolean }): Promise<T>;
    post<T>(url: string, body?: unknown, options?: { suppressGlobalErrorToast?: boolean }): Promise<T>;
  };
  units: UnitsSettings;
  canManage: boolean;
  onClose: () => void;
  onPersonChanged: () => Promise<void> | void;
};

function privacyStatusTag(status: PrivacyStatus, text: (key: string) => string) {
  if (status === PRIVACY_STATUS.anonymized) return <Tag color="default">{text("anonymized")}</Tag>;
  if (status === PRIVACY_STATUS.erasureRequested) return <Tag color="orange">{text("Erasure requested")}</Tag>;
  return <Tag color="green">{text("normal")}</Tag>;
}

function formatOptionalDate(value: string | null | undefined, units: UnitsSettings): string {
  return value ? formatDateTimeIso(value, units, { dateStyle: "short", timeStyle: "short" }) : "—";
}

function privacyChannelLabel(channel: string | null | undefined, text: (key: string) => string): string {
  if (channel === PRIVACY_CHANNEL.studio) return text("Studio");
  if (channel === PRIVACY_CHANNEL.app) return text("App");
  if (channel === PRIVACY_CHANNEL.web) return text("Web");
  return channel || "—";
}

export function PersonPrivacyDrawer({ open, personId, personName, api, units, canManage, onClose, onPersonChanged }: PersonPrivacyDrawerProps) {
  const { text } = useI18n();
  const { modal } = App.useApp();
  const feedback = useFeedback();
  const navigate = useNavigate();
  const [savingAction, setSavingAction] = React.useState<string | null>(null);
  const queryClient = useQueryClient();

  const summaryQuery = useQuery({
    queryKey: ["persons", personId ?? "", "privacy-summary"],
    queryFn: () =>
      api.get<PersonPrivacySummary>(`/api/v1/persons/${encodeURIComponent(personId ?? "")}/privacy-summary`, {
        suppressGlobalErrorToast: true
      }),
    enabled: open && !!personId
  });
  const summary = summaryQuery.data ?? null;
  const loading = summaryQuery.isFetching;


  const runAction = React.useCallback(
    async (action: "notice" | "request" | "complete") => {
      if (!personId || !canManage) return;
      if (action === "request") {
        const confirmed = await new Promise<boolean>((resolve) => {
          modal.confirm({
            title: text("Mark erasure request?"),
            content: text("Mark erasure request text"),
            okText: text("Mark request"),
            cancelText: text("Cancel"),
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
          });
        });
        if (!confirmed) return;
      }
      if (action === "complete") {
        const confirmed = await new Promise<boolean>((resolve) => {
          modal.confirm({
            title: text("Complete anonymization?"),
            content: text("Complete anonymization text"),
            okText: text("Complete anonymization"),
            okButtonProps: { danger: true },
            cancelText: text("Cancel"),
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
          });
        });
        if (!confirmed) return;
      }

      setSavingAction(action);
      try {
        const endpoint =
          action === "notice"
            ? "privacy-notice-received"
            : action === "request"
              ? "privacy-erasure-request"
              : "privacy-complete-anonymization";
        const body = action === "notice" ? { channel: "studio" } : {};
        const data = await api.post<PersonPrivacySummary>(`/api/v1/persons/${encodeURIComponent(personId)}/${endpoint}`, body);
        // Die Antwort ist der neue Stand — direkt in den Cache legen statt neu abzurufen.
        queryClient.setQueryData(["persons", personId, "privacy-summary"], data);
        await onPersonChanged();
        feedback.success(action === "complete" ? text("Anonymization completed.") : text("Saved."));
      } finally {
        setSavingAction(null);
      }
    },
    [api, canManage, feedback, modal, onPersonChanged, personId, queryClient, text]
  );

  const hasOpenRequest = !!summary?.openRequest;
  const isAnonymized = summary?.status === PRIVACY_STATUS.anonymized;
  const canWrite = canManage && !isAnonymized;

  return (
    <FormDrawer mode="view"
      title="Privacy"
      open={open}
      onCancel={onClose}
      contentLoading={loading && !summary}
      submitting={savingAction != null}
      width={drawerDefaults.width}
       dirty={false}    drawerProps={{ destroyOnClose: drawerDefaults.destroyOnClose }}
    >
        <Flex vertical gap={16}>
          <div>
            <Typography.Text type="secondary">{text("Person")}</Typography.Text>
            <Typography.Title level={5} style={{ marginTop: designSpacing.micro, marginBottom: 0 }}>{personName || text("(no name)")}</Typography.Title>
          </div>

          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label={text("Privacy status")}>
              {privacyStatusTag(summary?.status || "normal", text)}
            </Descriptions.Item>
            <Descriptions.Item label={text("Privacy notice received")}>
              {formatOptionalDate(summary?.privacyNoticeReceivedAt, units)}
            </Descriptions.Item>
            <Descriptions.Item label={text("Channel")}>
              {privacyChannelLabel(summary?.privacyNoticeChannel, text)}
            </Descriptions.Item>
            <Descriptions.Item label={text("User")}>
              {summary?.privacyNoticeReceivedByUserName || summary?.privacyNoticeReceivedByUserId || "—"}
            </Descriptions.Item>
            <Descriptions.Item label={text("Erasure requested at")}>
              {formatOptionalDate(summary?.erasureRequestedAt, units)}
            </Descriptions.Item>
            <Descriptions.Item label={text("Anonymized at")}>
              {formatOptionalDate(summary?.anonymizedAt, units)}
            </Descriptions.Item>
          </Descriptions>

          {summary?.openTask ? (
            <Alert
              type="warning"
              showIcon
              message={summary.openTask.title || text("Privacy task")}
              description={summary.openTask.description || text("Privacy task is open.")}
            />
          ) : null}

          {!canManage ? (
            <Alert type="info" showIcon message={text("No permission for privacy actions.")} />
          ) : null}

          {/* Höchstens drei Knöpfe: Schreibaktionen nur mit Recht, Löschwunsch vermerken und
              Anonymisierung abschließen schließen sich über den offenen Löschwunsch gegenseitig aus. */}
          <Space wrap>
            {canWrite ? (
              <Button
                icon={<SafetyCertificateOutlined />}
                loading={savingAction === PRIVACY_ACTION.notice}
                onClick={() => runAction("notice").catch(() => {})}
              >
                {text("Document privacy notice")}
              </Button>
            ) : null}
            {canWrite && !hasOpenRequest ? (
              <Button
                icon={<ClockCircleOutlined />}
                loading={savingAction === PRIVACY_ACTION.request}
                onClick={() => runAction("request").catch(() => {})}
              >
                {text("Mark erasure request")}
              </Button>
            ) : null}
            {summary?.openTask ? (
              <Button
                onClick={() => navigate("/tasks", { state: { openTaskId: summary.openTask?.id } })}
              >
                {text("Edit privacy task")}
              </Button>
            ) : null}
            {canWrite && hasOpenRequest ? (
              <Button
                danger
                type="primary"
                icon={<CheckOutlined />}
                loading={savingAction === PRIVACY_ACTION.complete}
                onClick={() => runAction("complete").catch(() => {})}
              >
                {text("Complete anonymization")}
              </Button>
            ) : null}
          </Space>
        </Flex>
    </FormDrawer>
  );
}
